import { create } from 'zustand';
import { api } from '../services/apiService';
import type { ContentItem, ContentStats } from '../types/content';

interface ContentFilters {
  status?: string;
  content_type?: string;
  channel?: string;
}

interface ContentState {
  items: ContentItem[];
  stats: ContentStats | null;
  loading: boolean;
  filters: ContentFilters;
  fetchPipeline: () => Promise<void>;
  fetchStats: () => Promise<void>;
  createContent: (data: Partial<ContentItem>) => Promise<ContentItem>;
  updateContent: (id: number, data: Partial<ContentItem>) => Promise<ContentItem>;
  deleteContent: (id: number) => Promise<void>;
  setFilters: (filters: Partial<ContentFilters>) => void;
}

function buildQueryString(filters: ContentFilters): string {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.content_type) params.set('content_type', filters.content_type);
  if (filters.channel) params.set('channel', filters.channel);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export const useContentStore = create<ContentState>((set, get) => ({
  items: [],
  stats: null,
  loading: false,
  filters: {},

  fetchPipeline: async () => {
    try {
      set({ loading: true });
      const qs = buildQueryString(get().filters);
      const items = await api.get<ContentItem[]>(`/content/pipeline${qs}`);
      set({ items, loading: false });
    } catch (err) {
      console.error('Failed to fetch content pipeline:', err);
      set({ loading: false });
    }
  },

  fetchStats: async () => {
    try {
      const stats = await api.get<ContentStats>('/content/stats');
      set({ stats });
    } catch (err) {
      console.error('Failed to fetch content stats:', err);
    }
  },

  createContent: async (data: Partial<ContentItem>) => {
    const item = await api.post<ContentItem>('/content', data);
    await get().fetchPipeline();
    await get().fetchStats();
    return item;
  },

  updateContent: async (id: number, data: Partial<ContentItem>) => {
    const item = await api.patch<ContentItem>(`/content/${id}`, data);
    await get().fetchPipeline();
    await get().fetchStats();
    return item;
  },

  deleteContent: async (id: number) => {
    await api.delete(`/content/${id}`);
    await get().fetchPipeline();
    await get().fetchStats();
  },

  setFilters: (newFilters: Partial<ContentFilters>) => {
    set((state) => ({
      filters: { ...state.filters, ...newFilters },
    }));
    get().fetchPipeline();
  },
}));
